import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Building2, ArrowLeft, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/tyretrack`;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const redirectTimerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [partitaIva, setPartitaIva] = useState("");
  const [responseMessage, setResponseMessage] = useState("");

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!partitaIva) {
      toast.error("Inserisci la Partita IVA");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/recovery/${partitaIva}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      const message = data.message || "Richiesta elaborata";

      if (response.status === 200) {
        toast.success(
          "Email inviata! Controlla la tua casella di posta per recuperare la password (verifica anche nello Spam).",
          { duration: 5000 }
        );
        redirectTimerRef.current = setTimeout(() => {
          navigate("/login");
        }, 2500);
      } else {
        setResponseMessage(message);
        setSubmitted(true);
        toast.error(message);
      }
    } catch (error) {
      console.error("Password recovery error:", error);
      toast.error("Errore di connessione. Riprova più tardi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      data-testid="forgot-password-page" 
      className="min-h-screen flex items-center justify-center p-4 bg-background"
    >
      <Card className="w-full max-w-md bg-card border-border">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4">
            <h1 className="text-3xl font-bold tracking-tight">
              <span className="text-primary">Tyre</span>Track
            </h1>
          </div>
          <CardTitle className="text-xl">Recupero Password</CardTitle>
          <CardDescription>
            Inserisci la tua Partita IVA per recuperare la password
          </CardDescription>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="space-y-4">
              <div className="p-4 bg-secondary rounded-lg">
                <p className="text-center text-foreground">{responseMessage}</p>
              </div>
              <Button
                onClick={() => {
                  setSubmitted(false);
                  setPartitaIva("");
                  setResponseMessage("");
                }}
                variant="secondary"
                className="w-full h-12"
              >
                Nuova richiesta
              </Button>
              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                >
                  <ArrowLeft size={14} />
                  Torna al Login
                </Link>
              </div>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="partitaIva" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                    Partita IVA
                  </Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      id="partitaIva"
                      name="partitaIva"
                      type="text"
                      data-testid="forgot-partita-iva"
                      placeholder="12345678901"
                      value={partitaIva}
                      onChange={(e) => setPartitaIva(e.target.value)}
                      className="pl-10 h-12 bg-input/50 border-input font-mono"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="forgot-submit"
                  disabled={loading}
                  className="w-full h-12 font-semibold bg-primary text-primary-foreground hover:bg-primary/90 btn-glow"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <KeyRound size={18} className="mr-2" />
                      Recupera Password
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  data-testid="back-to-login"
                  className="text-sm text-muted-foreground hover:text-primary hover:underline inline-flex items-center gap-1"
                >
                  <ArrowLeft size={14} />
                  Torna al Login
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
